import { Injectable, Logger, Inject, OnModuleInit, OnModuleDestroy } from '@nestjs/common';
import {
  RequestContextService,
  HTTP_CLIENT_FACTORY,
  type PlatformHttpClient,
  type HttpClientFactory,
  type HttpClientWithInterceptors,
} from '@lark-apaas/nestjs-common';
import * as fs from 'node:fs';
import * as path from 'node:path';
import * as chokidar from 'chokidar';
import type {
  CapabilityConfig,
  JSONSchema,
  PluginActionContext,
  PluginInstance,
  UserContext,
  StreamEvent,
} from '../interfaces';
import { PluginLoaderService } from './plugin-loader.service';
import { TemplateEngineService } from './template-engine.service';
import { TelemetryService, type CallerType } from './telemetry.service';
import { stringifyForLog } from '../utils';
import { formatPluginError } from '../utils/plugin-error-format';

export class CapabilityNotFoundError extends Error {
  readonly capabilityId: string;

  constructor(capabilityId: string) {
    super(`Capability not found: ${capabilityId}`);
    this.name = 'CapabilityNotFoundError';
    this.capabilityId = capabilityId;
  }
}

export class ActionNotFoundError extends Error {
  readonly pluginKey: string;
  readonly actionName: string;

  constructor(pluginKey: string, actionName: string) {
    super(`Action '${actionName}' not found in plugin ${pluginKey}`);
    this.name = 'ActionNotFoundError';
    this.pluginKey = pluginKey;
    this.actionName = actionName;
  }
}

export interface ParamDiagnostic {
  field: string;
  reason: 'not_provided' | 'empty_value';
  detail: string;
}

export class RequiredParamMissingError extends Error {
  readonly capabilityName: string;
  readonly diagnostics: ParamDiagnostic[];

  constructor(capabilityName: string, diagnostics: ParamDiagnostic[]) {
    const lines = diagnostics.map(d => `  - ${d.field}: ${d.detail}`).join('\n');
    super(
      `「${capabilityName}」插件调用失败，以下必填参数无效:\n${lines}\n` +
      '请检查调用代码中 capabilityClient.load(id).call(action, input) 的 input 对象，' +
      '确认上述参数已正确传值且未在传递链路中丢失。',
    );
    this.name = 'RequiredParamMissingError';
    this.capabilityName = capabilityName;
    this.diagnostics = diagnostics;
  }
}

export interface CapabilityExecutor {
  /**
   * 调用 capability（始终返回 Promise）
   * - unary action: 直接返回结果
   * - stream action: 内部聚合所有 chunk 后返回
   */
  call(actionName: string, input: unknown, context?: Partial<PluginActionContext>): Promise<unknown>;

  /**
   * 流式调用 capability，返回原始流
   * - 返回原始 AsyncIterable
   * - 如果 action 是 unary，包装为单次 yield
   */
  callStream(actionName: string, input: unknown, context?: Partial<PluginActionContext>): AsyncIterable<unknown>;

  /**
   * 流式调用 capability，返回带事件协议的流（推荐）
   * - 返回 StreamEvent 类型的 AsyncIterable
   * - 支持 data/done/error 三种事件类型
   * - Controller 层应优先使用此方法实现边收边发
   */
  callStreamWithEvents(
    actionName: string,
    input: unknown,
    context?: Partial<PluginActionContext>,
  ): AsyncIterable<StreamEvent<unknown>>;

  /**
   * 检查 action 是否为流式
   */
  isStream(actionName: string): Promise<boolean>;
}

export interface CapabilityModuleOptions {
  /** 能力配置目录路径，默认 server/capabilities */
  capabilitiesDir?: string;
  /** 是否启用文件监听（热更新），默认 false */
  enableWatching?: boolean;
  /** 文件变更防抖时间（ms），默认 300 */
  watchDebounce?: number;
}

@Injectable()
export class CapabilityService implements OnModuleInit, OnModuleDestroy {
  private readonly logger = new Logger(CapabilityService.name);
  private readonly capabilities = new Map<string, CapabilityConfig>();
  /** 文件路径到 capability id 的映射，用于文件删除时查找 */
  private readonly filePathToId = new Map<string, string>();
  private capabilitiesDir: string;
  private fileWatcher: chokidar.FSWatcher | null = null;
  private options: CapabilityModuleOptions = {};

  constructor(
    private readonly requestContextService: RequestContextService,
    @Inject(HTTP_CLIENT_FACTORY) private readonly httpClientFactory: HttpClientFactory,
    private readonly pluginLoaderService: PluginLoaderService,
    private readonly templateEngineService: TemplateEngineService,
    private readonly telemetryService: TelemetryService,
  ) {
    this.capabilitiesDir = path.join(process.cwd(), 'server/capabilities');
  }

  /**
   * 设置模块配置
   */
  setOptions(options: CapabilityModuleOptions): void {
    this.options = options;
    if (options.capabilitiesDir) {
      this.capabilitiesDir = options.capabilitiesDir;
    }
  }

  setCapabilitiesDir(dir: string): void {
    this.capabilitiesDir = dir;
  }

  async onModuleInit(): Promise<void> {
    await this.loadCapabilities();

    // 如果启用文件监听，开始监听
    if (this.options.enableWatching) {
      this.startWatching();
    }
  }

  async onModuleDestroy(): Promise<void> {
    this.stopWatching();
  }

  // ==================== 文件监听相关方法 ====================

  /**
   * 启动文件监听（沙箱环境自动调用）
   */
  startWatching(): void {
    if (this.fileWatcher) {
      return; // 已在监听
    }

    if (!fs.existsSync(this.capabilitiesDir)) {
      this.logger.warn(`Cannot start watching: directory not found: ${this.capabilitiesDir}`);
      return;
    }

    const pattern = path.join(this.capabilitiesDir, '*.json');
    const debounce = this.options.watchDebounce ?? 300;

    this.logger.log(`Starting file watcher: ${pattern}`);

    this.fileWatcher = chokidar.watch(pattern, {
      persistent: true,
      ignoreInitial: true, // 忽略初始扫描
      awaitWriteFinish: {
        // 等待写入完成
        stabilityThreshold: debounce,
        pollInterval: 100,
      },
    });

    this.fileWatcher
      .on('add', filePath => this.handleFileAdd(filePath))
      .on('change', filePath => this.handleFileChange(filePath))
      .on('unlink', filePath => this.handleFileUnlink(filePath))
      .on('error', error => this.logger.error('File watcher error:', error));

    this.logger.log('File watcher started');
  }

  /**
   * 停止文件监听
   */
  stopWatching(): void {
    if (this.fileWatcher) {
      this.fileWatcher.close();
      this.fileWatcher = null;
      this.logger.log('File watcher stopped');
    }
  }

  /**
   * 重新加载所有能力配置
   */
  async reloadAllCapabilities(): Promise<void> {
    this.capabilities.clear();
    this.filePathToId.clear();
    await this.loadCapabilities();
  }

  private handleFileAdd(filePath: string): void {
    this.logger.log(`Capability file added: ${filePath}`);
    try {
      this.loadCapabilityFromFile(filePath);

      // 清除关联插件的缓存，确保加载最新版本
      const capabilityId = this.filePathToId.get(filePath);
      if (capabilityId) {
        const config = this.capabilities.get(capabilityId);
        if (config?.pluginKey) {
          this.pluginLoaderService.clearCache(config.pluginKey);
          this.logger.log(`Cleared plugin cache for new capability: ${config.pluginKey}`);
        }
      }
    } catch (error) {
      this.logger.error(`Failed to load new capability: ${filePath}`, error);
    }
  }

  private handleFileChange(filePath: string): void {
    this.logger.log(`Capability file changed: ${filePath}`);
    try {
      this.reloadCapabilityFromFile(filePath);
    } catch (error) {
      this.logger.error(`Failed to reload capability: ${filePath}`, error);
    }
  }

  private handleFileUnlink(filePath: string): void {
    this.logger.log(`Capability file removed: ${filePath}`);
    this.removeCapabilityByFile(filePath);
  }

  /**
   * 从文件加载单个能力配置
   */
  private loadCapabilityFromFile(filePath: string): void {
    const content = fs.readFileSync(filePath, 'utf-8');
    const config = JSON.parse(content) as CapabilityConfig;

    if (!config.id) {
      this.logger.warn(`Skipping capability without id: ${filePath}`);
      return;
    }

    this.capabilities.set(config.id, config);
    this.filePathToId.set(filePath, config.id);
    this.logger.log(`Loaded capability: ${config.id} (${config.name})`);
  }

  /**
   * 重新加载单个能力配置
   */
  private reloadCapabilityFromFile(filePath: string): void {
    // 先移除旧配置（如果存在）
    const oldId = this.filePathToId.get(filePath);
    if (oldId) {
      const oldConfig = this.capabilities.get(oldId);
      if (oldConfig) {
        // 清除关联插件的缓存
        this.pluginLoaderService.clearCache(oldConfig.pluginKey);
      }
      this.capabilities.delete(oldId);
    }

    // 重新加载
    this.loadCapabilityFromFile(filePath);
  }

  /**
   * 根据文件路径移除能力配置
   */
  private removeCapabilityByFile(filePath: string): void {
    const capabilityId = this.filePathToId.get(filePath);
    if (capabilityId) {
      this.capabilities.delete(capabilityId);
      this.filePathToId.delete(filePath);
      this.logger.log(`Removed capability: ${capabilityId}`);
    }
  }

  // ==================== 原有方法 ====================

  private async loadCapabilities(): Promise<void> {
    this.logger.log(`Loading capabilities from ${this.capabilitiesDir}`);

    if (!fs.existsSync(this.capabilitiesDir)) {
      this.logger.warn(`Capabilities directory not found: ${this.capabilitiesDir}`);
      return;
    }

    const files = fs.readdirSync(this.capabilitiesDir).filter(f => f.endsWith('.json'));

    for (const file of files) {
      try {
        const filePath = path.join(this.capabilitiesDir, file);
        this.loadCapabilityFromFile(filePath);
      } catch (error) {
        this.logger.error(`Failed to load capability from ${file}:`, error);
      }
    }

    this.logger.log(`Loaded ${this.capabilities.size} capabilities`);
  }

  listCapabilities(): CapabilityConfig[] {
    return Array.from(this.capabilities.values());
  }

  getCapability(capabilityId: string): CapabilityConfig | null {
    return this.capabilities.get(capabilityId) ?? null;
  }

  load(capabilityId: string): CapabilityExecutor {
    const config = this.capabilities.get(capabilityId);
    if (!config) {
      throw new CapabilityNotFoundError(capabilityId);
    }

    return this.createExecutor(config);
  }

  /**
   * 使用传入的配置加载能力执行器
   * 用于 debug 场景，支持用户传入自定义配置
   */
  loadWithConfig(config: CapabilityConfig): CapabilityExecutor {
    return this.createExecutor(config);
  }

  private createExecutor(config: CapabilityConfig): CapabilityExecutor {
    return {
      call: async (
        actionName: string,
        input: unknown,
        contextOverride?: Partial<PluginActionContext>,
      ) => {
        return this.executeCall(config, actionName, input, contextOverride);
      },

      callStream: (
        actionName: string,
        input: unknown,
        contextOverride?: Partial<PluginActionContext>,
      ) => {
        return this.executeCallStream(config, actionName, input, contextOverride);
      },

      callStreamWithEvents: (
        actionName: string,
        input: unknown,
        contextOverride?: Partial<PluginActionContext>,
      ) => {
        return this.executeCallStreamWithEvents(config, actionName, input, contextOverride);
      },

      isStream: async (actionName: string) => {
        return this.checkIsStream(config, actionName);
      },
    };
  }

  /**
   * 加载插件实例并解析参数
   * 根据 manifest 的 form.refType 决定 formValue 的消费方式
   */
  private async loadPluginAndResolveParams(
    config: CapabilityConfig,
    input: unknown,
  ): Promise<{ pluginInstance: PluginInstance; resolvedParams: unknown }> {
    const isConfigRequired = this.pluginLoaderService.isConfigRequired(config.pluginKey);

    if (isConfigRequired) {
      // refType: "config" - formValue 作为 config 传给 create()
      const pluginInstance = await this.pluginLoaderService.loadPlugin(
        config.pluginKey,
        config.formValue,
      );
      // 直接使用 input 作为 action 参数（不经过模板引擎）
      return { pluginInstance, resolvedParams: input };
    }

    // refType: "action" 或默认 - 保持现有行为
    const pluginInstance = await this.pluginLoaderService.loadPlugin(config.pluginKey);
    const resolvedParams = config.formValue
      ? this.templateEngineService.resolve(config.formValue, input as Record<string, unknown>, config.paramsSchema)
      : input;
    return { pluginInstance, resolvedParams };
  }

  /**
   * 校验调用方传入的 input 是否满足 paramsSchema.required 约束。
   *
   * 注意：校验发生在模板替换之前。paramsSchema.required 声明的是调用方需要传哪些变量
   * （它们会被 formValue 中的 {{input.xxx}} 引用），不是 resolve 后的 formValue key。
   *
   * Debug 调用（isDebug=true）跳过校验：开发者可能在 paramSchema 里声明了字段但表单
   * 里并未通过 {{input.xxx}} 实际引用，此时 required 校验会误报；debug 阶段交由
   * 插件自身的 zod schema 兜底即可。
   *
   * 诊断原因：
   *   not_provided — input 中完全没有该字段（key 不存在 / undefined / null），调用方未传
   *   empty_value  — 调用方传了值但为空（空串/空数组），需要检查数据来源
   */
  private validateRequiredParams(
    input: unknown,
    paramsSchema: JSONSchema | undefined,
    capabilityName: string,
    isDebug: boolean,
  ): void {
    if (isDebug) return;
    if (!paramsSchema?.required?.length || !paramsSchema.properties) {
      return;
    }
    if (typeof input !== 'object' || input === null) {
      return;
    }

    const params = input as Record<string, unknown>;
    const diagnostics: ParamDiagnostic[] = [];

    for (const fieldName of paramsSchema.required) {
      const fieldSchema = paramsSchema.properties[fieldName];
      if (!fieldSchema) continue;

      const value = params[fieldName];
      const schemaType = fieldSchema.type;
      const desc = (fieldSchema.description as string) || fieldName;

      // 1. 完全没传（undefined / null）
      if (value === undefined || value === null) {
        diagnostics.push({
          field: fieldName,
          reason: 'not_provided',
          detail:
            `「${desc}」未传值，调用方的 input 中缺少「${fieldName}」字段。` +
            '请检查 capabilityClient.load(id).call(action, { ... }) 中是否传入了该参数，' +
            '以及数据在组件传递链路中是否丢失（如 state 未更新、props 未透传等）。',
        });
        continue;
      }

      // 2. 传了但为空值
      if (schemaType === 'array') {
        if (Array.isArray(value) && value.length === 0) {
          diagnostics.push({
            field: fieldName,
            reason: 'empty_value',
            detail:
              `「${desc}」传入了空数组 []，该字段为必填。` +
              '请确认数据来源是否正确，例如：列表接口是否返回了数据、文件/图片是否已成功上传。',
          });
        }
      } else if (typeof value === 'string' && value === '') {
        diagnostics.push({
          field: fieldName,
          reason: 'empty_value',
          detail:
            `「${desc}」传入了空字符串，该字段为必填。` +
            '请确认数据来源是否正确，例如：表单字段是否已填写、变量是否已赋值。',
        });
      }
    }

    if (diagnostics.length > 0) {
      throw new RequiredParamMissingError(capabilityName, diagnostics);
    }
  }

  /**
   * 检查 action 是否为流式
   */
  private async checkIsStream(config: CapabilityConfig, actionName: string): Promise<boolean> {
    const { pluginInstance } = await this.loadPluginAndResolveParams(config, {});

    if (!pluginInstance.hasAction(actionName)) {
      throw new ActionNotFoundError(config.pluginKey, actionName);
    }

    return pluginInstance.isStreamAction?.(actionName) ?? false;
  }

  /**
   * 执行 capability（始终返回 Promise）
   * - unary action: 直接返回结果
   * - stream action: 内部聚合所有 chunk 后返回
   */
  private async executeCall(
    config: CapabilityConfig,
    actionName: string,
    input: unknown,
    contextOverride?: Partial<PluginActionContext>,
  ): Promise<unknown> {
    const startTime = Date.now();
    const loggerContext = {
      capability_id: config.id,
      plugin_key: config.pluginKey,
      action: actionName,
    };
    let isStream = false;
    try {
      this.validateRequiredParams(input, config.paramsSchema, config.name, contextOverride?.isDebug ?? false);
      const { pluginInstance, resolvedParams } = await this.loadPluginAndResolveParams(config, input);

      if (!pluginInstance.hasAction(actionName)) {
        throw new ActionNotFoundError(config.pluginKey, actionName);
      }

      const context = this.buildActionContext(config.pluginKey, contextOverride);
      isStream = pluginInstance.isStreamAction?.(actionName) ?? false;

      // 打印入参
      this.logger.log('Executing capability (call)', {
        ...loggerContext,
        is_stream: isStream,
        input: stringifyForLog(input),
      });

      let result: unknown;

      if (isStream && pluginInstance.runStream) {
        // 流式 action：聚合所有 chunk
        const chunks: unknown[] = [];
        for await (const chunk of pluginInstance.runStream(actionName, context, resolvedParams)) {
          chunks.push(chunk);
        }
        // 使用插件的聚合方法，或默认返回 chunks 数组
        result = pluginInstance.aggregate
          ? pluginInstance.aggregate(actionName, chunks)
          : chunks;
      } else {
        // 非流式 action：直接调用
        result = await pluginInstance.run(actionName, context, resolvedParams);
      }

      const durationMs = Date.now() - startTime;

      // 打印返回值
      this.logger.log('Capability (call) executed successfully', {
        ...loggerContext,
        duration_ms: durationMs,
        output: stringifyForLog(result),
      });

      this.reportCallEvent(config.pluginKey, true, durationMs, isStream, contextOverride);

      return result;
    } catch (error) {
      const durationMs = Date.now() - startTime;

      this.logger.error('Capability (call) execution failed', {
        ...loggerContext,
        duration_ms: durationMs,
        error: error instanceof Error ? error.message : String(error),
      });

      const errorCode = String((error as any)?.code ?? 'EXECUTION_ERROR');
      this.reportCallEvent(config.pluginKey, false, durationMs, isStream, contextOverride, errorCode);

      // 将 PluginError 的 message 格式化为中文，controller 的 buildErrorResponse 会直接使用
      const displayName = this.pluginLoaderService.getManifest(config.pluginKey)?.displayName;
      const formattedMessage = formatPluginError(error, displayName);
      if (formattedMessage && error instanceof Error) {
        error.message = formattedMessage;
      }

      throw error;
    }
  }

  /**
   * 流式执行 capability
   * - stream action: 返回原始 AsyncIterable
   * - unary action: 包装为单次 yield
   */
  private async *executeCallStream(
    config: CapabilityConfig,
    actionName: string,
    input: unknown,
    contextOverride?: Partial<PluginActionContext>,
  ): AsyncIterable<unknown> {
    const startTime = Date.now();
    const loggerContext = {
      capability_id: config.id,
      plugin_key: config.pluginKey,
      action: actionName,
    };
    const chunks: unknown[] = [];
    try {
      this.validateRequiredParams(input, config.paramsSchema, config.name, contextOverride?.isDebug ?? false);
      const { pluginInstance, resolvedParams } = await this.loadPluginAndResolveParams(config, input);

      if (!pluginInstance.hasAction(actionName)) {
        throw new ActionNotFoundError(config.pluginKey, actionName);
      }

      const context = this.buildActionContext(config.pluginKey, contextOverride);
      const isStream = pluginInstance.isStreamAction?.(actionName) ?? false;

      // 打印入参
      this.logger.log('Executing capability (stream)', {
        ...loggerContext,
        is_stream: isStream,
        input: stringifyForLog(input),
      });

      if (isStream && pluginInstance.runStream) {
        // 流式 action：透传 AsyncIterable，同时收集 chunks
        for await (const chunk of pluginInstance.runStream(actionName, context, resolvedParams)) {
          chunks.push(chunk);
          yield chunk;
        }
      } else {
        // 非流式 action：包装为单次 yield
        const result = await pluginInstance.run(actionName, context, resolvedParams);
        chunks.push(result);
        yield result;
      }

      const durationMs = Date.now() - startTime;

      // 打印聚合结果
      const aggregatedResult = pluginInstance.aggregate
        ? pluginInstance.aggregate(actionName, chunks)
        : chunks;
      this.logger.log('Capability (stream) executed successfully', {
        ...loggerContext,
        duration_ms: durationMs,
        output: stringifyForLog(aggregatedResult),
      });

      this.reportCallEvent(config.pluginKey, true, durationMs, isStream, contextOverride);
    } catch (error) {
      const durationMs = Date.now() - startTime;

      this.logger.error('Capability (stream) execution failed', {
        ...loggerContext,
        duration_ms: durationMs,
        error: error instanceof Error ? error.message : String(error),
      });

      const errorCode = String((error as any)?.code ?? 'EXECUTION_ERROR');
      this.reportCallEvent(config.pluginKey, false, durationMs, true, contextOverride, errorCode);

      const displayName = this.pluginLoaderService.getManifest(config.pluginKey)?.displayName;
      const formattedMessage = formatPluginError(error, displayName);
      if (formattedMessage && error instanceof Error) {
        error.message = formattedMessage;
      }

      throw error;
    }
  }

  /**
   * 流式执行 capability，返回带事件协议的流
   * - 优先使用 pluginInstance.runStreamWithEvents
   * - 如果插件不支持，则包装 runStream/run 为 StreamEvent
   */
  private async *executeCallStreamWithEvents(
    config: CapabilityConfig,
    actionName: string,
    input: unknown,
    contextOverride?: Partial<PluginActionContext>,
  ): AsyncIterable<StreamEvent<unknown>> {
    const startTime = Date.now();
    const loggerContext = {
      capability_id: config.id,
      plugin_key: config.pluginKey,
      action: actionName,
    };
    const chunks: unknown[] = [];
    try {
      this.validateRequiredParams(input, config.paramsSchema, config.name, contextOverride?.isDebug ?? false);
      const { pluginInstance, resolvedParams } = await this.loadPluginAndResolveParams(config, input);

      if (!pluginInstance.hasAction(actionName)) {
        throw new ActionNotFoundError(config.pluginKey, actionName);
      }

      const context = this.buildActionContext(config.pluginKey, contextOverride);
      const isStream = pluginInstance.isStreamAction?.(actionName) ?? false;

      // 打印入参
      this.logger.log('Executing capability (streamWithEvents)', {
        ...loggerContext,
        is_stream: isStream,
        input: stringifyForLog(input),
      });

      // 优先使用 runStreamWithEvents
      if (pluginInstance.runStreamWithEvents) {
        for await (const event of pluginInstance.runStreamWithEvents(actionName, context, resolvedParams)) {
          // 收集 data 事件的数据用于聚合
          if (event.type === 'data') {
            chunks.push(event.data);
            yield event;
          } else if (event.type === 'done') {
            // 拦截 done 事件，添加聚合结果
            const aggregatedResult = pluginInstance.aggregate
              ? pluginInstance.aggregate(actionName, chunks)
              : chunks;

            // 上报必须在 yield done 之前，因为 controller 收到 done 后会 return 导致 generator 终止
            const durationMs = Date.now() - startTime;
            this.reportCallEvent(config.pluginKey, true, durationMs, isStream, contextOverride);

            yield {
              type: 'done',
              metadata: {
                ...event.metadata,
                aggregated: aggregatedResult,
              },
            };
          } else {
            yield event;
          }
        }
      } else {
        // 回退：包装 runStream 或 run 为 StreamEvent
        if (isStream && pluginInstance.runStream) {
          // 流式 action：包装每个 chunk 为 data 事件
          for await (const chunk of pluginInstance.runStream(actionName, context, resolvedParams)) {
            chunks.push(chunk);
            yield { type: 'data', data: chunk };
          }
        } else {
          // 非流式 action：包装结果为单个 data 事件
          const result = await pluginInstance.run(actionName, context, resolvedParams);
          chunks.push(result);
          yield { type: 'data', data: result };
        }

        // 聚合结果
        const aggregatedResult = pluginInstance.aggregate
          ? pluginInstance.aggregate(actionName, chunks)
          : chunks;

        // 上报必须在 yield done 之前
        const durationMs = Date.now() - startTime;
        this.reportCallEvent(config.pluginKey, true, durationMs, isStream, contextOverride);

        // 发送 done 事件（包含聚合结果）
        yield {
          type: 'done',
          metadata: {
            chunks: chunks.length,
            duration: durationMs,
            aggregated: aggregatedResult,
          },
        };
      }

      // 打印聚合结果
      const aggregatedResult = pluginInstance.aggregate
        ? pluginInstance.aggregate(actionName, chunks)
        : chunks;
      this.logger.log('Capability (streamWithEvents) executed successfully', {
        ...loggerContext,
        duration_ms: Date.now() - startTime,
        output: stringifyForLog(aggregatedResult),
      });
    } catch (error) {
      const durationMs = Date.now() - startTime;

      this.logger.error('Capability (streamWithEvents) execution failed', {
        ...loggerContext,
        duration_ms: durationMs,
        error: error instanceof Error ? error.message : String(error),
      });

      // 上报必须在 yield error 之前
      const errorCode = String((error as any)?.code ?? 'EXECUTION_ERROR');
      this.reportCallEvent(config.pluginKey, false, durationMs, true, contextOverride, errorCode);

      // 发送 error 事件
      yield {
        type: 'error',
        error: this.extractErrorInfo(error, config.pluginKey),
      };
    }
  }

  /**
   * 上报调用事件（fire-and-forget）
   */
  private reportCallEvent(
    pluginKey: string,
    success: boolean,
    durationMs: number,
    isStream: boolean,
    contextOverride?: Partial<PluginActionContext>,
    statusCode = '0',
  ): void {
    const callerType: CallerType = contextOverride?.caller
      ?? (contextOverride?.isDebug ? 'debug' : 'server');
    const manifest = this.pluginLoaderService.getManifest(pluginKey);
    this.telemetryService.reportCall(pluginKey, {
      version: manifest?.version ?? '',
      success: String(success),
      duration: String(durationMs),
      is_stream: String(isStream),
      caller_type: String(callerType),
      status_code: statusCode,
    }).catch(() => {
      // 静默忽略上报失败
    });
  }

  /**
   * 从错误对象提取错误信息
   * 支持 PluginError 和 RateLimitError
   * @param error - 捕获到的错误
   * @param pluginKey - 插件 key，用于获取 displayName 格式化 PluginError 消息
   */
  private extractErrorInfo(error: unknown, pluginKey?: string): {
    code: string;
    message: string;
    rateLimitCode?: string;
    rateLimitMessage?: string;
  } {
    const err = error as any;

    // 获取错误码，默认为 EXECUTION_ERROR
    const code = err?.code ?? 'EXECUTION_ERROR';
    const message = err instanceof Error ? err.message : String(err);

    // 如果是 RateLimitError，透传业务 code 和 message
    if (code === 'RATE_LIMIT_EXCEEDED' && err?.rateLimitCode) {
      return {
        code,
        message,
        rateLimitCode: err.rateLimitCode,
        rateLimitMessage: err.rateLimitMessage,
      };
    }

    // 对 PluginError 格式化为中文消息
    if (pluginKey) {
      const displayName = this.pluginLoaderService.getManifest(pluginKey)?.displayName;
      const formattedMessage = formatPluginError(error, displayName);
      if (formattedMessage) {
        return { code, message: formattedMessage };
      }
    }

    return { code, message };
  }

  private buildActionContext(
    pluginKey: string,
    override?: Partial<PluginActionContext>,
  ): PluginActionContext {
    return {
      pluginKey,
      logger: this.logger,
      platformHttpClient: this.createPluginHttpClient(pluginKey),
      userContext: override?.userContext ?? this.getUserContext(),
      isDebug: override?.isDebug ?? false,
      caller: override?.caller,
    };
  }

  /**
   * 为指定插件创建独立的 HttpClient 实例
   * 自动添加 x-plugin-key header
   */
  private createPluginHttpClient(pluginKey: string): PlatformHttpClient {
    const client = this.httpClientFactory.create();

    // 添加请求拦截器，注入 x-plugin-key header
    client.interceptors.request.use((config) => {
      config.headers = {
        ...config.headers,
        'x-plugin-key': pluginKey,
      };
      return config;
    });

    // 返回受保护的包装器，不暴露 interceptors
    return this.wrapAsProtectedClient(client);
  }

  /**
   * 将 HttpClient 包装为受保护的 PlatformHttpClient
   * 只暴露请求方法，不暴露 interceptors
   */
  private wrapAsProtectedClient(client: HttpClientWithInterceptors): PlatformHttpClient {
    return {
      request: (config: any) => client.request(config),
      get: (url: string, config?: any) => client.get(url, config),
      post: (url: string, data?: any, config?: any) => client.post(url, data, config),
      put: (url: string, data?: any, config?: any) => client.put(url, data, config),
      patch: (url: string, data?: any, config?: any) => client.patch(url, data, config),
      delete: (url: string, config?: any) => client.delete(url, config),
    };
  }

  private getUserContext(): UserContext {
    const ctx = this.requestContextService.getContext();
    return {
      appId: ctx?.appId ?? '',
      userId: ctx?.userId ?? '',
      tenantId: ctx?.tenantId ?? '',
      isSystemAccount: ctx?.isSystemAccount ?? false,
    };
  }
}
